'use client';

import { type ComponentProps, type FC, type SVGProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type FieldError } from 'react-hook-form';
import { match } from 'ts-pattern';

import { useDropdown, type UseDropdownConfiguration } from '../../../../hooks/helpers/useDropdown';
import { useOnClickOutside } from '../../../../hooks/helpers/useOnClickOutside';
import { useRTL } from '../../../../hooks/helpers/useRTL';
import { useWindowSize } from '../../../../hooks/helpers/useWindowSize';
import { type MobileModalWrapperProps } from '../../../../models/modal';
import { Divider } from '../../../atoms/divider/Divider';
import { MenuItem } from '../../../atoms/menu-item/MenuItem';
import { MobileModalWrapper } from '../../../molecules/modal/MobileModalWrapper';
import BaseSearchInput from '../search-input/BaseSearchInput';
import { BaseTextInput } from '../text-input/BaseTextInput';

import { S } from './BaseSelectInput.styles';

const dropdownMinWidth = 300;

export type BaseSelectInputProps<T> = {
	customLabelProp?: (countryIso: T) => React.ReactNode;
	data: Array<T>;
	detached?: MobileModalWrapperProps['detached'];
	disabled?: boolean;
	dropdownConfiguration?: UseDropdownConfiguration;
	emptyListMessage?: string;
	error?: FieldError;
	Icon?: FC<SVGProps<SVGSVGElement>>;
	isOpen?: boolean;
	itemTemplate: (params: { item: T }) => React.ReactNode;
	itemTextEllipsis?: boolean;
	label?: string;
	labelProp?: keyof T;
	onChange?: (value: T) => void;
	onClose?: () => void;
	onOpen?: () => void;
	onSearch: (items: Array<T>, text: string) => Array<T>;
	placeholder?: string;
	readonly?: boolean;
	searchPlaceholder?: string;
	supportingText?: string;
	testID: string;
	title?: string;
	value?: null | T;
	withExtraBottomSheetPadding?: boolean;
	withSearch?: boolean;
} & Omit<ComponentProps<typeof S.BaseSelectContainer>, 'onChange'>;

/**
 * BaseSelectInput is a component, which displays a list of items by clicking on a component
 *
 * @param {string} props.testID - TestID
 * @param {Array<T>} props.data - Array of items
 * @param {(items: Array<T>, text: string) => Array<T>} props.onSearch - Callback that is called when the text input's text changes and return search items
 * @param {(params: { item: T }) => string} props.itemTemplate - The text that is displayed in the element
 * @param {T | null} props.value - Initial selected value
 * @param {() => void} props.isOpen - If true, immediately display the modal with items
 * @param {boolean} props.onOpen - The callback function that is called after opening the modal
 * @param {boolean} props.onClose - The callback function that is called after closing the modal
 * @param {(value: T) => void} props.onChange - The callback function to be called on item press
 * @param {FieldError} props.error - Can be used to activate the on-error style
 * @param {string} props.emptyListMessage - The text that is displayed when items are not found by search
 * @param {keyof T} props.labelProp - The label to display, which is a key of T
 * @param {keyof T} props.customLabelProp - The callback function that is called to display a custom label
 * @param {title} props.title - The title to display in mobile modal
 * @param {boolean} props.withSearch - If true, enable search input
 * @param {boolean} props.disabled - If true, disabled SelectInput
 * @param {UseDropdownConfiguration} props.dropdownConfiguration - Dropdown configuration
 * @param {FC<SVGProps<SVGSVGElement>>} props.Icon - The Icon to display in selectInput
 * @returns {JSX.Element} The BaseSelectInput component
 */
export const BaseSelectInput = <T,>({
	customLabelProp,
	data,
	detached,
	disabled,
	dropdownConfiguration = {},
	emptyListMessage,
	error,
	Icon,
	isOpen = false,
	itemTemplate,
	itemTextEllipsis,
	label,
	labelProp,
	onChange,
	onClose,
	onOpen,
	onSearch,
	placeholder,
	readonly,
	searchPlaceholder,
	supportingText,
	tabIndex,
	testID,
	title,
	value,
	withExtraBottomSheetPadding = true,
	withSearch,
	...props
}: BaseSelectInputProps<T>) => {
	const [isModalOpen, setIsModalOpen] = useState<boolean>(isOpen);
	const [search, setSearch] = useState<string>('');
	const listItems = useMemo(() => onSearch(data, search), [data, search, onSearch]);
	const isDropdownVisibleRef = useRef(false);
	const spacePressedRef = useRef<(event: KeyboardEvent) => void>();

	const containerRef = useRef<HTMLDivElement>(null);
	const { isRTL } = useRTL();

	const { size } = useWindowSize();

	// Dropdown Hook
	const dropdown = useRef<HTMLUListElement>(null);
	const activator = useRef<HTMLDivElement>(null);

	const defaultDropdownConfiguration: UseDropdownConfiguration = {
		horizontalAlignment: 'start',
		negativeHorizontalPadding: 0,
		verticalAlignment: 'bottom'
	};

	const {
		dropdownOpacity,
		dropdownPosition,
		hideDropdown,
		isDropdownVisible,
		openDropdown,
		recalculatePosition: recalculateDropdownVerticalPosition
	} = useDropdown(
		{
			activator,
			dropdown
		},
		{
			...defaultDropdownConfiguration,
			...dropdownConfiguration
		}
	);

	const handleOpen = useCallback(() => {
		setIsModalOpen(true);
		onOpen?.();
	}, [onOpen]);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		onClose?.();
	}, [onClose]);

	const handleSearch = useCallback(
		(text: string) => {
			setSearch(text);
		},
		[setSearch]
	);

	useEffect(() => {
		if (size === 'desktop') recalculateDropdownVerticalPosition();
	}, [listItems.length, size]);

	useOnClickOutside(containerRef, () => {
		handleCloseModal();
		hideDropdown();
	});

	useEffect(() => {
		if (isModalOpen) {
			handleOpen();
			openDropdown();
		} else {
			handleCloseModal();
		}
	}, [handleCloseModal, handleOpen, isModalOpen, openDropdown]);

	useEffect(() => {
		if (!isModalOpen) {
			setSearch('');
		}
	}, [search, value, setSearch, isModalOpen]);

	const ListEmptyComponent = useCallback(
		() => (
			<S.EmptyListWrapper
				$minWidth={dropdownMinWidth}
				$position={dropdownPosition}
				data-testid={`${testID}-empty`}
			>
				<S.EmptyText>{emptyListMessage}</S.EmptyText>
			</S.EmptyListWrapper>
		),
		[emptyListMessage, testID, dropdownPosition]
	);

	useEffect(() => {
		isDropdownVisibleRef.current = isDropdownVisible;
	}, [isDropdownVisible]);

	const onItemPress = useCallback(
		(item: T) => {
			onChange?.(item);
			handleCloseModal();
		},
		[handleCloseModal, onChange]
	);

	const renderItem = useCallback(
		(item: T, index: number) => (
			<MenuItem
				key={`${testID}-${index}`}
				testID={`${testID}-item--${index}`}
				textEllipsis={itemTextEllipsis}
				title={itemTemplate({ item })}
				onClick={() => onItemPress(item)}
			/>
		),
		[testID, itemTemplate, onItemPress, itemTextEllipsis]
	);

	const renderItems = useMemo(() => {
		return listItems.map((item, index) => {
			return (
				<div key={`${itemTemplate({ item })}-${index}`}>
					{renderItem(item, index)}
					{index + 1 < listItems.length && <Divider orientation="horizontal" />}
				</div>
			);
		});
	}, [listItems, renderItem]);

	const labelText: string = useMemo(
		() => String(value ? (labelProp ? value[labelProp] : '') : placeholder ?? ''),
		[value, labelProp, placeholder]
	);

	const stopDragPropagation = useCallback((ev: React.SyntheticEvent) => {
		ev.stopPropagation();
	}, []);

	const selectBottomSheetDOM = useMemo(
		() => (
			<S.BottomSheetContainer $extraPadding={withExtraBottomSheetPadding}>
				<S.BottomSheetTitle>{title}</S.BottomSheetTitle>
				{withSearch && (
					<BaseSearchInput
						aria-label={`${testID}-mobile-input`}
						autoFocus
						placeholder={searchPlaceholder}
						testID={`${testID}-mobile-input`}
						value={search}
						onChange={e => handleSearch(e.target.value)}
					/>
				)}
				{listItems.length === 0 && emptyListMessage && <S.EmptyText>{emptyListMessage}</S.EmptyText>}

				{match(listItems.length).otherwise(() => (
					<S.BaseSelectListMobile
						aria-label={`${testID}-list`}
						data-testid={`${testID}-list`}
						onPointerDownCapture={stopDragPropagation}
						onTouchStartCapture={stopDragPropagation}
					>
						{listItems.map((item, index) => (
							<S.BaseSelectListItem
								aria-label={`${testID}-item`}
								data-testid={`${testID}-item`}
								key={index}
							>
								<>
									{renderItem(item, index)}
									{index + 1 < listItems.length && <Divider orientation="horizontal" />}
								</>
							</S.BaseSelectListItem>
						))}
					</S.BaseSelectListMobile>
				))}
			</S.BottomSheetContainer>
		),
		[
			listItems,
			withExtraBottomSheetPadding,
			title,
			withSearch,
			search,
			searchPlaceholder,
			testID,
			emptyListMessage,
			handleSearch,
			stopDragPropagation,
			renderItem
		]
	);

	const spacePressed = useCallback(
		(event: KeyboardEvent) => {
			if (event.code === 'Space' && !isDropdownVisibleRef.current) {
				event.stopPropagation();
				event.preventDefault();
				handleOpen();
			}
		},
		[handleOpen, isDropdownVisible]
	);

	const addSpaceListener = useCallback(() => {
		removeSpaceListener();
		spacePressedRef.current = spacePressed;
		document.addEventListener('keydown', spacePressed);
	}, [spacePressed, isDropdownVisible, spacePressedRef.current]);

	const removeSpaceListener = useCallback(() => {
		if (spacePressedRef.current) {
			document.removeEventListener('keydown', spacePressedRef.current);
		}
	}, []);

	const tabPressed = useCallback(
		(event: KeyboardEvent) => {
			if (event.code === 'Tab') {
				removeSpaceListener();
				handleCloseModal();
				hideDropdown();
			}
		},
		[handleCloseModal, hideDropdown]
	);

	useEffect(() => {
		if (tabIndex !== undefined) {
			document.addEventListener('keydown', tabPressed);
		}

		return () => document.removeEventListener('keydown', tabPressed);
	}, [tabIndex]);

	return (
		<S.BaseSelectContainer
			{...props}
			$disabled={disabled}
			$isReadonly={readonly}
			data-testid={testID}
			ref={containerRef}
		>
			<S.BaseSelect
				aria-label={`${testID}-input-container`}
				data-testid={`${testID}-input-container`}
				ref={activator}
				onClick={handleOpen}
			>
				{isModalOpen ? (
					withSearch ? (
						<BaseTextInput
							autoFocus
							placeholder={searchPlaceholder}
							testID={`${testID}-input`}
							value={search}
							onBlur={removeSpaceListener}
							onChange={e => handleSearch(e.target.value)}
							onFocus={() => window.dispatchEvent(new Event('resize'))}
						/>
					) : (
						<S.InputWrapper tabIndex={tabIndex} onBlur={removeSpaceListener} onFocus={addSpaceListener}>
							<S.InputContent $label={Boolean(label)}>
								{label && <S.FloatingLabel $disabled={disabled}>{label}</S.FloatingLabel>}
								<S.BaseSelectedOption
									$disabled={disabled}
									$isPlaceholder={value ? false : !!placeholder}
								>
									{customLabelProp && value ? customLabelProp(value) : labelText}
								</S.BaseSelectedOption>
							</S.InputContent>
							{Icon && <Icon height={24} width={24} />}
						</S.InputWrapper>
					)
				) : (
					<S.InputWrapper
						$hasError={!!error}
						tabIndex={tabIndex}
						onBlur={removeSpaceListener}
						onFocus={addSpaceListener}
					>
						<S.InputContent $label={Boolean(label)}>
							{label && <S.FloatingLabel $disabled={disabled}>{label}</S.FloatingLabel>}
							<S.BaseSelectedOption $disabled={disabled} $isPlaceholder={value ? false : !!placeholder}>
								{customLabelProp && value ? customLabelProp(value) : labelText}
							</S.BaseSelectedOption>
						</S.InputContent>
						{Icon && <Icon height={24} width={24} />}
					</S.InputWrapper>
				)}
				<S.BottomWrapper $isRTL={isRTL}>
					{(error || supportingText) && (
						<S.SupportingText $disabled={disabled} $hasError={!!error}>
							{error?.message ?? supportingText}
						</S.SupportingText>
					)}
				</S.BottomWrapper>
			</S.BaseSelect>
			{isModalOpen &&
				match(size)
					.with('desktop', () =>
						match(listItems.length)
							.with(0, () => emptyListMessage && <ListEmptyComponent />)
							.otherwise(() => (
								<S.Dropdown
									$minWidth={dropdownMinWidth}
									$opacity={dropdownOpacity}
									$position={dropdownPosition}
									ref={dropdown}
									role="list"
									style={isModalOpen ? { display: 'block' } : { display: 'none' }}
								>
									{renderItems}
								</S.Dropdown>
							))
					)
					.otherwise(() => (
						<MobileModalWrapper
							child={selectBottomSheetDOM}
							closeModal={handleCloseModal}
							detached={detached}
							inView={isModalOpen}
							justClosed={false}
						/>
					))}
		</S.BaseSelectContainer>
	);
};
